/**
 * Thin fetch wrapper for the admin SPA.
 *
 * - Always sends credentials (the audit_session cookie is httpOnly, same-origin).
 * - On 401, redirects the whole browser to the OIDC login (the BFF), preserving
 *   where the user was trying to go via ?return_to=. This is a hard navigation,
 *   not a router push, because login lives on the API origin.
 * - Throws ApiError on non-2xx so callers can render error states.
 */

import {
  ADMIN_ROUTES,
  AUTH_ROUTES,
  type AdminClientDetail,
  type AuthMe,
  type Client,
  type CreateClientRequest,
  type CreateClientResponse,
  type EnrollmentTokenResponse,
  type ListAdminSessionsResponse,
  type ListClientsResponse,
  type SessionDetail,
  type UpdateClientRequest,
} from '@opsrev/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Hard-redirect to the login flow, remembering the current location. */
export function redirectToLogin(): void {
  const returnTo = window.location.pathname + window.location.search;
  const url = `${AUTH_ROUTES.LOGIN}?return_to=${encodeURIComponent(returnTo)}`;
  window.location.assign(url);
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** When true, a 401 will NOT auto-redirect (used by the auth bootstrap). */
  noAuthRedirect?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: {},
  };

  if (opts.body !== undefined) {
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    throw new ApiError(0, `Network error: ${(err as Error).message}`, null);
  }

  if (res.status === 401 && !opts.noAuthRedirect) {
    redirectToLogin();
    // Surface an error so in-flight callers stop; navigation is already underway.
    throw new ApiError(401, 'Not authenticated', null);
  }

  if (!res.ok) {
    const body = await parseBody(res);
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await parseBody(res)) as T;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export function fetchMe(): Promise<AuthMe> {
  // noAuthRedirect: the AuthProvider decides how to handle the unauthenticated
  // case (it shows a login prompt / triggers the redirect itself).
  return request<AuthMe>(AUTH_ROUTES.ME, { noAuthRedirect: true });
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export function listClients(): Promise<ListClientsResponse> {
  return request<ListClientsResponse>(ADMIN_ROUTES.CLIENTS);
}

export function getClient(id: string): Promise<AdminClientDetail> {
  return request<AdminClientDetail>(ADMIN_ROUTES.client(id));
}

export function createClient(body: CreateClientRequest): Promise<CreateClientResponse> {
  return request<CreateClientResponse>(ADMIN_ROUTES.CLIENTS, { method: 'POST', body });
}

export function updateClient(id: string, body: UpdateClientRequest): Promise<Client> {
  return request<Client>(ADMIN_ROUTES.client(id), { method: 'PATCH', body });
}

export function rotateToken(id: string): Promise<EnrollmentTokenResponse> {
  return request<EnrollmentTokenResponse>(ADMIN_ROUTES.rotateToken(id), { method: 'POST' });
}

export function clientExportUrl(id: string): string {
  return `${ADMIN_ROUTES.clientExport(id)}?format=md`;
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export function listSessions(clientId?: string): Promise<ListAdminSessionsResponse> {
  const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  return request<ListAdminSessionsResponse>(`${ADMIN_ROUTES.SESSIONS}${qs}`);
}

export function getSession(id: string): Promise<SessionDetail> {
  return request<SessionDetail>(ADMIN_ROUTES.session(id));
}

export function reprocessSession(id: string): Promise<unknown> {
  return request<unknown>(ADMIN_ROUTES.sessionReprocess(id), { method: 'POST' });
}

/** The video endpoint is consumed directly by a <video src>, not via fetch. */
export function sessionVideoUrl(id: string): string {
  return ADMIN_ROUTES.sessionVideo(id);
}

export function sessionExportUrl(id: string): string {
  return `${ADMIN_ROUTES.sessionExport(id)}?format=md`;
}
